import {Messages} from "../messages/Messages";
import {BindingList, createHtmlElement, hasNoHidden} from "../lib/domFunctions";
import {ProcessControlService_Mode} from "../dto/com.rico.sb2.service";
import {SendTransportToPosition} from "../modals/SendTransportToPosition";
import {UpdateSchemeMessage, UpdateTransportMessage, visitUpdateSchemeMessage} from "../dto/com.rico.sb2.message";
import {TransportAction, TransportStateFlag} from "../dto/com.rico.sb2.entity.device";
import {SendTransportTake} from "../modals/SendTransportTake";
import {SendTransportPut} from "../modals/SendTransportPut";
import {PlayerDataAdapter, TransportBadgeListener, UpdateSchemeMessageConsumer} from "../PlayerDataAdapter";
import {Popover, PopoverInstance} from "../lib/bootstrapPopover";
import {escapeHTML} from "../lib/escapeHTML";
import {apiPost} from "../lib/SecuredAjax";
import {AppConfig} from "../AppConfig";
import {showToastInfo} from "../lib/boostrapToast";
import {toastFetchError} from "../lib/fetch";

const messages = new Messages();

export class TransportPopover implements UpdateSchemeMessageConsumer, PopoverInstance, TransportBadgeListener {
	private readonly playerData: PlayerDataAdapter
	private readonly id: number

	private titleElement: HTMLElement | null = null
	private bodyElement: HTMLElement | null = null
	private bindings = new BindingList();

	private popover: Popover | null = null

	constructor(playerData: PlayerDataAdapter, id: number) {
		this.playerData = playerData
		this.id = id
	}

	private get transport() {
		return this.playerData.getTransportData(this.id);
	}

	title(): string | Element {
		const transport = this.transport
		const textType = messages.get(`dashboard.modal.${transport.type.toLowerCase()}.title`, transport.code);
		const actionState = transport.action == null ? `` : `<span class="badge text-bg-info text-lowercase ms-3 fw-normal">${messages.get(`transport.action.${transport.action}`)}</span>`;
		this.titleElement = createHtmlElement('div', {class: "d-flex"}, `<div>${textType}</div><div class="flex-fill"></div><div data-bind="stateBadge"></div>${actionState}`)
		this.updateTitle()
		return this.titleElement
	}

	content(): string | Element {
		return this.createBody() || ''
	}

	onSchemeUpdate(update: UpdateSchemeMessage): void {
		visitUpdateSchemeMessage(update, {
			transportMessage: m => this.onSchemeTransportUpdate(m)
		})
	}

	private onSchemeTransportUpdate(update: UpdateTransportMessage) {
		if (update.id != this.id) return;

		this.updateBody();
		this.updateTitle();
		this.popover?.update();
	}

	onTransportBadgeChange(transport: number): void {
		if (!(transport == 0 || transport == this.id)) return;

		this.updateBody();
		this.popover?.update();
	}

	private createBody(): Element | null {
		this.bodyElement = createHtmlElement('div', {}, `
            <div class="mb-n2">
                <table class="w-100">
                    <tr class="align-baseline">
                        <td class="text-nowrap pe-4">${messages.get('TransportPopover.label.code')}</td>
                        <td class="text-end" data-bind="code"></td>
                    </tr>
                    <tr class="align-baseline">
                        <td class="text-nowrap pe-4">${messages.get('TransportPopover.label.number')}</td>
                        <td class="text-end" data-bind="number"></td>
                    </tr>
                    <tr class="align-baseline">
                        <td class="text-nowrap pe-4">${messages.get('TransportPopover.label.id')}</td>
                        <td class="text-end" data-bind="id"></td>
                    </tr>
                    <tr class="align-baseline" data-bind="positionInfo">
                        <td class="text-nowrap pe-4">${messages.get('TransportPopover.label.position')}</td>
                        <td class="text-end" data-bind="position"></td>
                    </tr>
                    <tr class="align-baseline" data-bind="positionTypeInfo">
                        <td class="text-nowrap pe-4">${messages.get('TransportPopover.label.positionType')}</td>
                        <td class="text-end" data-bind="positionType"></td>
                    </tr>
                </table>
				<table class="w-100" data-bind="badgesAll"></table>

                <div class="d-flex align-items-baseline justify-content-between mt-3" style="margin-left: -.25rem; margin-right: -.25rem; margin-bottom: -.5rem;" data-bind="operations">
                    <div class="flex-fill"></div>
                    <button type="button" class="btn btn-danger btn-sm mb-2 mx-1 text-nowrap" data-bind="actionBlockOn">${messages.get('TransportPopover.action.blockOn')}</button>
                    <button type="button" class="btn btn-danger btn-sm mb-2 mx-1 text-nowrap" data-bind="actionBlockOff">${messages.get('TransportPopover.action.blockOff')}</button>
                    <button type="button" class="btn btn-primary btn-sm mb-2 mx-1 text-nowrap" data-bind="actionTake">${messages.get('TransportPopover.action.take')}</button>
                    <button type="button" class="btn btn-primary btn-sm mb-2 mx-1 text-nowrap" data-bind="actionPut">${messages.get('TransportPopover.action.put')}</button>
                    <button type="button" class="btn btn-primary btn-sm mb-2 mx-1 text-nowrap" data-bind="actionMove">${messages.get('TransportPopover.action.move')}</button>
                </div>
            </div>
        `)

		this.bindings.collect(this.bodyElement);
		this.bindings.update('actionMove', button => button.addEventListener('click', () => this.move()))
		this.bindings.update('actionTake', button => button.addEventListener('click', () => this.take()))
		this.bindings.update('actionPut', button => button.addEventListener('click', () => this.put()))
		this.bindings.update('actionBlockOn', button => button.addEventListener('click', () => this.block(true)))
		this.bindings.update('actionBlockOff', button => button.addEventListener('click', () => this.block(false)))

		this.updateBody();

		return this.bodyElement;
	}

	private updateTitle() {
		const transport = this.transport

		let statusBadgeColor = '';
		if (transport.state == TransportStateFlag.DISABLED) {
			statusBadgeColor = `text-bg-warning`;

		} else if (transport.state == TransportStateFlag.BLOCKED) {
			statusBadgeColor = `text-bg-danger`;
		}

		let statusBadge = !statusBadgeColor ? `` : `<span class="badge ${statusBadgeColor} text-lowercase ms-3 fw-normal">${messages.get(`transport.state.${transport.state}`)}</span>`;

		const statusBadgeContainer = this.titleElement?.querySelector('[data-bind="stateBadge"]');
		if (statusBadgeContainer) {
			statusBadgeContainer.innerHTML = statusBadge;
		}
	}

	private updateBody() {
		const body = this.bodyElement
		if (!body) return

		const transport = this.transport
		this.bindings.update('id', node => node.textContent = this.id.toString())
		this.bindings.update('number', node => node.textContent = transport.number.toString())
		this.bindings.update('code', node => node.textContent = transport.code)

		const position = this.transport.position;

		let positionInfo = false
		if (position != null) {
			positionInfo = true
			this.bindings.update('position', node => node.textContent = this.transport.positionCode)
			this.bindings.update('positionType', node => node.textContent = this.transport.positionTitle)
		}
		this.bindings.toggle('positionInfo', positionInfo);
		this.bindings.toggle('positionTypeInfo', positionInfo);

		const moveModeForEnabled = new Set<ProcessControlService_Mode | null>([ProcessControlService_Mode.MANUAL, ProcessControlService_Mode.PAUSED])
		const moveModeForBlocked = new Set<ProcessControlService_Mode | null>([ProcessControlService_Mode.MANUAL, ProcessControlService_Mode.PAUSED, ProcessControlService_Mode.AUTOMATIC, ProcessControlService_Mode.SEMIAUTOMATIC])

		const moveAvailable =
			(transport.enabled && moveModeForEnabled.has(transport.serviceMode)) ||
			(transport.state == TransportStateFlag.BLOCKED && moveModeForBlocked.has(transport.serviceMode));

		this.bindings.toggle('actionMove', moveAvailable && transport.action == TransportAction.STOPPED);
		this.bindings.toggle('actionTake', moveAvailable && transport.action == TransportAction.STOPPED && transport.type == 'AO' && transport.container == null && transport.containerBelow != null);
		this.bindings.toggle('actionPut', moveAvailable && transport.action == TransportAction.STOPPED && transport.type == 'AO' && transport.container != null && transport.containerBelow == null);

		this.bindings.toggle('actionBlockOn', transport.state == TransportStateFlag.ENABLED && transport.action == TransportAction.STOPPED);
		this.bindings.toggle('actionBlockOff', transport.state == TransportStateFlag.BLOCKED && transport.action == TransportAction.STOPPED);

		this.bindings.toggle('operations', this.bindings.someAny(['actionMove', 'actionTake', 'actionPut', 'actionBlockOn', 'actionBlockOff'], hasNoHidden));

		this.bindings.update('badgesAll', node => {
			node.hidden = transport.badges.length == 0;
			node.innerHTML = `<tr><td colspan="2"><hr class="my-2"></td></tr>` + transport.badges
				.map(badge => `<tr><td class="text-nowrap">${escapeHTML(badge.label)}</td><td class="text-end ps-3">${badge.html}</td></tr>`)
				.join('')
		})
	}

	private move() {
		new SendTransportToPosition(this.id, this.transport.code, null).showModal();
	}

	private take() {
		new SendTransportTake(this.id, this.transport.code).showModal();
	}

	private put() {
		new SendTransportPut(this.id, this.transport.code).showModal();
	}

	private block(on: boolean) {
		apiPost(`${AppConfig.CP}/transports/${this.id}/switchState`, on ? `"BLOCKED"` : `"ENABLED"`)
			.then(success => {
				if (!success) return Promise.reject(500)

				showToastInfo(messages.get(on ? 'SendTransportBlock.completeOn' : 'SendTransportBlock.completeOff'))

			})
			.catch(toastFetchError);
	}

	createPopover(target: Element, rootContainer: HTMLElement): Popover {
		this.playerData.updateListeners.add(this)
		this.playerData.transportBadgeListeners.add(this)

		const options: Partial<Popover.Options> = {
			animation: false, html: true, trigger: 'manual', sanitize: false,
			container: rootContainer,
			// да, такого свойства нет. но оно все равно работает
			// @ts-ignore
			customClass: () => function (templateFactory: any): string {
				if (templateFactory && templateFactory._config && templateFactory._config.content && templateFactory._config.content['.popover-body'] == '') {
					return 'dashboard-popover popover-header-only'
				}
				return 'dashboard-popover'
			},
			content: () => this.content(),
			title: () => this.title(),
		};

		return this.popover = new Popover(target, options);
	}

	hidePopover(): void {
		this.playerData.updateListeners.remove(this)
		this.playerData.transportBadgeListeners.remove(this)
	}
}